import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Landmark, Wallet, FileText, Truck, Users2, IndianRupee, Download, Printer,
  Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import { exportRowsToCSV } from "@/lib/csvExport";
import { toast } from "sonner";
import { toLocalISODate } from "@/lib/dateUtils";

const money = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = toLocalISODate;

const presets = () => {
  const now = new Date();
  return {
    month: [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)],
    quarter: [iso(new Date(now.getFullYear(), now.getMonth() - 2, 1)), iso(now)],
    year: [iso(new Date(now.getFullYear(), 0, 1)), iso(now)],
  };
};

const statusColor = (s) => {
  const v = String(s || "").toLowerCase();
  if (["paid", "delivered", "completed"].includes(v)) return "bg-emerald-100 text-emerald-700";
  if (["pending", "sent", "ordered", "confirmed"].includes(v)) return "bg-amber-100 text-amber-700";
  if (["overdue", "cancelled"].includes(v)) return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
};

const StatRow = ({ label, value, sub, positive }) => (
  <div className="flex items-center justify-between py-2 border-b last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
    <p className={`text-base font-bold ${positive === true ? 'text-emerald-700' : positive === false ? 'text-red-700' : 'text-gray-900'}`}>
      {money(value)}
    </p>
  </div>
);

const SectionCard = ({ icon: Icon, title, accent, children, testId, full }) => (
  <Card className={`border-${accent}-200 ${full ? 'lg:col-span-2' : ''}`} data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">
        <Icon className={`h-4 w-4 text-${accent}-600`} /> {title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const DetailToggle = ({ open, setOpen, count, testId }) => (
  <button
    type="button"
    onClick={() => setOpen(!open)}
    className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 mt-3 mb-1 print:hidden"
    data-testid={testId}
  >
    {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
    {open ? "Hide" : "Show"} itemized details ({count})
  </button>
);

const DetailTable = ({ headers, rows, emptyLabel }) => (
  <div className="overflow-x-auto max-h-64 overflow-y-auto border rounded-md mt-1 print:max-h-none print:overflow-visible">
    <table className="w-full text-xs">
      <thead className="bg-gray-50 sticky top-0">
        <tr>
          {headers.map((h) => (
            <th key={h} className="text-left px-2 py-1.5 font-medium text-gray-500 whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={headers.length} className="px-2 py-3 text-gray-400 text-center">{emptyLabel}</td></tr>
        ) : rows}
      </tbody>
    </table>
  </div>
);

const CompleteFinancialStatement = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const p = presets();
  const [range, setRange] = useState(p.month);
  const [active, setActive] = useState("month");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showInvoices, setShowInvoices] = useState(true);
  const [showPOs, setShowPOs] = useState(true);
  const [showSalaries, setShowSalaries] = useState(true);
  const [showExpenses, setShowExpenses] = useState(true);

  const applyPreset = (key) => { const pr = presets(); setActive(key); setRange(pr[key]); };

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const [start, end] = range;
      const res = await axios.get(`${backendUrl}/api/accounting/full-statement?start_date=${start}&end_date=${end}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load financial statement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatement(); }, [range]);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = ["Category", "Metric", "Amount"];
    const rows = [
      ["Cash & Bank", "Total Bank Balance (current)", data.cash_bank.total_bank_balance],
      ["Cash & Bank", "Cash in Hand (current)", data.cash_bank.cash_balance],
      ["Cash & Bank", "Total Cash + Bank Position", data.cash_bank.total_cash_position],
      ["Revenue", `Invoices Sent (${data.receivables.invoices_sent_count})`, data.receivables.invoices_sent_amount],
      ["Revenue", "Outstanding Receivables", data.receivables.outstanding_receivables],
      ["Payables", `Purchase Orders (${data.payables.purchase_orders_count})`, data.payables.purchase_orders_amount],
      ["Payables", "Supplier Payments Made (in range)", data.payables.supplier_payments_in_range],
      ["Payables", "Outstanding Payables", data.payables.outstanding_payables],
      ["Outflows", "Parts Consumed (COGS)", data.payables.parts_consumed_amount],
      ["Outflows", `Salaries Paid (${data.salaries.paid_count})`, data.salaries.paid_in_range],
      ["Outflows", `Salaries Pending (${data.salaries.pending_count})`, data.salaries.pending_total],
      ["Outflows", `Garage Expenses (${data.garage_expenses.count})`, data.garage_expenses.total_in_range],
      ["Summary", "Total Inflow (Invoices Sent)", data.summary.total_inflow_in_range],
      ["Summary", "Total Outflow (Parts Consumed + Salaries + Expenses)", data.summary.total_outflow_in_range],
      ["Summary", "Net For Period", data.summary.net_for_range],
      [],
      ["--- Invoices ---"],
      ["Invoice #", "Customer", "Date", "Status", "Amount"],
      ...data.receivables.invoices.map((i) => [i.invoice_number, i.customer_name, i.date, i.status, i.total_amount]),
      [],
      ["--- Purchase Orders ---"],
      ["PO #", "Supplier", "Date", "Status", "Amount"],
      ...data.payables.purchase_orders.map((o) => [o.po_number, o.supplier_name, o.date, o.status, o.total_amount]),
      [],
      ["--- Salaries ---"],
      ["Employee", "Month", "Status", "Amount"],
      ...data.salaries.records.map((s) => [s.employee_name, s.month, s.status, s.net_salary]),
      [],
      ["--- Garage Expenses ---"],
      ["Date", "Category", "Vendor", "Description", "Amount"],
      ...data.garage_expenses.rows.map((e) => [e.date, e.category, e.vendor, e.title || e.description, e.amount]),
    ];
    exportRowsToCSV(`Complete_Financial_Statement_${range[0]}_to_${range[1]}`, headers, rows);
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6 mt-6" data-testid="full-financial-statement">
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <h2 className="text-lg font-semibold">Complete Financial Statement</h2>
          <p className="text-xs text-gray-500">Bank, cash, invoices, purchase orders, salaries &amp; garage expenses in one view</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex gap-1">
            {["month", "quarter", "year"].map((k) => (
              <Button key={k} size="sm" variant={active === k ? "default" : "outline"} onClick={() => applyPreset(k)} data-testid={`fs-preset-${k}`}>
                {k === "month" ? "This Month" : k === "quarter" ? "3 Months" : "This Year"}
              </Button>
            ))}
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={range[0]} onChange={(e) => { setActive(""); setRange([e.target.value, range[1]]); }} data-testid="fs-from-date" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={range[1]} onChange={(e) => { setActive(""); setRange([range[0], e.target.value]); }} data-testid="fs-to-date" />
          </div>
          <Button variant="outline" onClick={handleExportCSV} data-testid="fs-export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={() => window.print()} data-testid="fs-print-btn">
            <Printer className="h-4 w-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <p className="text-xs text-gray-400 print:block hidden">
        Statement period: {range[0]} to {range[1]} &middot; Bank/Cash balances shown as of today
      </p>

      {/* Closing position summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-r from-slate-700 to-slate-900 text-white">
          <CardContent className="p-4">
            <p className="text-xs opacity-80">Total Cash + Bank Position</p>
            <p className="text-xl font-bold">{money(data.cash_bank.total_cash_position)}</p>
            <p className="text-[10px] opacity-60">As of today</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
          <CardContent className="p-4">
            <p className="text-xs opacity-80">Inflow (Invoices Sent)</p>
            <p className="text-xl font-bold">{money(data.summary.total_inflow_in_range)}</p>
            <p className="text-[10px] opacity-60">Selected period</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-red-600 to-red-700 text-white">
          <CardContent className="p-4">
            <p className="text-xs opacity-80">Outflow (Parts Consumed + Salaries + Expenses)</p>
            <p className="text-xl font-bold">{money(data.summary.total_outflow_in_range)}</p>
            <p className="text-[10px] opacity-60">Selected period</p>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-r ${data.summary.net_for_range >= 0 ? 'from-blue-600 to-blue-700' : 'from-orange-600 to-orange-700'} text-white`} data-testid="fs-net-card">
          <CardContent className="p-4">
            <p className="text-xs opacity-80">Net For Period</p>
            <p className="text-xl font-bold">{money(data.summary.net_for_range)}</p>
            <p className="text-[10px] opacity-60">Selected period</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard icon={Landmark} title="Bank Accounts" accent="blue" testId="fs-bank-section">
          {data.cash_bank.bank_accounts.length === 0 ? (
            <p className="text-sm text-gray-400">No bank accounts on record</p>
          ) : data.cash_bank.bank_accounts.map((a) => (
            <StatRow key={a.id} label={a.bank_name} sub={`A/C ${a.account_number}`} value={a.current_balance} />
          ))}
          <StatRow label="Total Bank Balance" value={data.cash_bank.total_bank_balance} />
        </SectionCard>

        <SectionCard icon={Wallet} title="Cash Account" accent="green" testId="fs-cash-section">
          <StatRow label="Cash in Hand" sub="Current balance" value={data.cash_bank.cash_balance} />
          <StatRow label="Combined Cash + Bank" value={data.cash_bank.total_cash_position} />
        </SectionCard>

        {/* Invoices & Receivables */}
        <SectionCard icon={FileText} title="Invoices & Receivables" accent="emerald" testId="fs-receivables-section" full>
          <StatRow label={`Invoices Sent (${data.receivables.invoices_sent_count})`} sub="Selected period" value={data.receivables.invoices_sent_amount} positive />
          <StatRow label="Outstanding Receivables" sub="From customers, as of today" value={data.receivables.outstanding_receivables} />
          {data.receivables.by_customer && data.receivables.by_customer.length > 0 && (
            <>
              <p className="text-xs font-medium text-gray-500 mt-3">Top Outstanding Customers</p>
              <DetailTable
                headers={["Customer", "Outstanding"]}
                rows={data.receivables.by_customer.slice(0, 10).map((c, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">{c.customer_name || c.name}</td>
                    <td className="px-2 py-1.5 font-semibold text-red-700">{money(c.outstanding || c.amount)}</td>
                  </tr>
                ))}
                emptyLabel="No outstanding customers"
              />
            </>
          )}
          <DetailToggle open={showInvoices} setOpen={setShowInvoices} count={data.receivables.invoices.length} testId="fs-toggle-invoices" />
          {showInvoices && (
            <DetailTable
              headers={["Invoice #", "Customer", "Date", "Status", "Amount"]}
              rows={data.receivables.invoices.map((inv, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1.5 whitespace-nowrap">{inv.invoice_number}</td>
                  <td className="px-2 py-1.5">{inv.customer_name}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{inv.date}</td>
                  <td className="px-2 py-1.5"><Badge className={`text-[10px] ${statusColor(inv.status)}`}>{inv.status}</Badge></td>
                  <td className="px-2 py-1.5 font-semibold">{money(inv.total_amount)}</td>
                </tr>
              ))}
              emptyLabel="No invoices sent in this period"
            />
          )}
        </SectionCard>

        {/* Purchase Orders & Payables */}
        <SectionCard icon={Truck} title="Purchase Orders & Payables" accent="orange" testId="fs-payables-section" full>
          <StatRow label={`Purchase Orders (${data.payables.purchase_orders_count})`} sub="Committed spend on stock — selected period" value={data.payables.purchase_orders_amount} positive={false} />
          <StatRow label="Parts Consumed (COGS)" sub="Cost of parts actually sold — used in Net Profit below" value={data.payables.parts_consumed_amount} positive={false} />
          <StatRow label="Supplier Payments Made" sub="Selected period" value={data.payables.supplier_payments_in_range} positive={false} />
          <StatRow label="Outstanding Payables" sub="To suppliers, as of today" value={data.payables.outstanding_payables} />
          {data.payables.by_supplier && data.payables.by_supplier.length > 0 && (
            <>
              <p className="text-xs font-medium text-gray-500 mt-3">Top Outstanding Suppliers</p>
              <DetailTable
                headers={["Supplier", "Outstanding"]}
                rows={data.payables.by_supplier.slice(0, 10).map((s, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">{s.supplier_name || s.name}</td>
                    <td className="px-2 py-1.5 font-semibold text-red-700">{money(s.outstanding || s.amount)}</td>
                  </tr>
                ))}
                emptyLabel="No outstanding suppliers"
              />
            </>
          )}
          <DetailToggle open={showPOs} setOpen={setShowPOs} count={data.payables.purchase_orders.length} testId="fs-toggle-pos" />
          {showPOs && (
            <DetailTable
              headers={["PO #", "Supplier", "Date", "Status", "Amount"]}
              rows={data.payables.purchase_orders.map((po, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1.5 whitespace-nowrap">{po.po_number}</td>
                  <td className="px-2 py-1.5">{po.supplier_name}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{po.date}</td>
                  <td className="px-2 py-1.5"><Badge className={`text-[10px] ${statusColor(po.status)}`}>{po.status}</Badge></td>
                  <td className="px-2 py-1.5 font-semibold">{money(po.total_amount)}</td>
                </tr>
              ))}
              emptyLabel="No purchase orders placed in this period"
            />
          )}
        </SectionCard>

        {/* Salaries */}
        <SectionCard icon={Users2} title="Salaries" accent="purple" testId="fs-salaries-section" full>
          <StatRow label={`Paid (${data.salaries.paid_count})`} sub="Selected period" value={data.salaries.paid_in_range} positive={false} />
          <StatRow label={`Pending (${data.salaries.pending_count})`} sub="All-time, unpaid" value={data.salaries.pending_total} />
          <DetailToggle open={showSalaries} setOpen={setShowSalaries} count={data.salaries.records.length} testId="fs-toggle-salaries" />
          {showSalaries && (
            <DetailTable
              headers={["Employee", "Month", "Status", "Amount"]}
              rows={data.salaries.records.map((s, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1.5">{s.employee_name}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{s.month}</td>
                  <td className="px-2 py-1.5"><Badge className={`text-[10px] ${statusColor(s.status)}`}>{s.status}</Badge></td>
                  <td className="px-2 py-1.5 font-semibold">{money(s.net_salary)}</td>
                </tr>
              ))}
              emptyLabel="No salary records"
            />
          )}
        </SectionCard>

        {/* Garage Expenses */}
        <SectionCard icon={IndianRupee} title="Garage Expenses" accent="red" testId="fs-expenses-section" full>
          <StatRow label={`Total Expenses (${data.garage_expenses.count})`} sub="Selected period" value={data.garage_expenses.total_in_range} positive={false} />
          <DetailToggle open={showExpenses} setOpen={setShowExpenses} count={data.garage_expenses.rows.length} testId="fs-toggle-expenses" />
          {showExpenses && (
            <DetailTable
              headers={["Date", "Category", "Vendor", "Description", "Amount"]}
              rows={data.garage_expenses.rows.map((e, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1.5 whitespace-nowrap">{e.date}</td>
                  <td className="px-2 py-1.5 capitalize">{String(e.category || '').replace(/_/g, ' ')}</td>
                  <td className="px-2 py-1.5">{e.vendor}</td>
                  <td className="px-2 py-1.5">{e.title || e.description}</td>
                  <td className="px-2 py-1.5 font-semibold">{money(e.amount)}</td>
                </tr>
              ))}
              emptyLabel="No garage expenses in this period"
            />
          )}
        </SectionCard>
      </div>
    </div>
  );
};

export default CompleteFinancialStatement;
